/**
 * Farm & Acre Co - Cart Shared Helpers
 *
 * Shared utilities, constants, and UI refresh functions for cart-related scripts.
 * This prevents code duplication and provides a single point of maintenance.
 *
 * Contents:
 * - Debug logging system
 * - Fee variant ID constants
 * - Cart section refresh (Dawn-native pattern)
 */

(function() {
  'use strict';

  // ===========================================================================
  // DEBUG SYSTEM
  // ===========================================================================

  const DEBUG = false; // Set to false to disable debug logging globally

  /**
   * Create a debug logger with a custom prefix
   * @param {string} prefix - The prefix for debug messages
   * @returns {Object} Object with debugLog, debugError, debugWarn functions
   */
  function createDebugLogger(prefix) {
    return {
      log: function(message, data = null) {
        if (DEBUG) {
          if (data !== null) {
            console.log(prefix, message, data);
          } else {
            console.log(prefix, message);
          }
        }
      },
      error: function(message, error = null) {
        if (DEBUG) {
          if (error !== null) {
            console.error(prefix, message, error);
          } else {
            console.error(prefix, message);
          }
        }
      },
      warn: function(message, data = null) {
        if (DEBUG) {
          if (data !== null) {
            console.warn(prefix, message, data);
          } else {
            console.warn(prefix, message);
          }
        }
      }
    };
  }

  // ===========================================================================
  // FEE VARIANT ID CONSTANTS
  // ===========================================================================

  const SA_LEVY_VARIANT_ID = '45235983941769';

  const SETUP_FEES = {
    'Standard Print': null,
    'Non-Standard Print': '45225654845577',
    'Complex Print': '45225664741513'
  };

  const LINE_FEES = {
    'blank': null,
    '1-line': '45246316544137',
    '2-lines': '45246316576905',
    '3-lines': '45246316609673',
    'custom': '45246316642441'
  };

  const SIDE_FEES = {
    'No Print': null,
    'Printed 1 Side': '45345057276041',
    'Printed 2 Sides': '45345057308809'
  };

  const ALL_SETUP_FEE_IDS = ['45225654845577', '45225664741513'];
  const ALL_LINE_FEE_IDS = ['45246316544137', '45246316576905', '45246316609673', '45246316642441'];
  const ALL_SIDE_FEE_IDS = ['45345057276041', '45345057308809'];

  // FOFIA print fees (laser vs pitch black) — one product "FOFIA Print Fee" (handle fofia-print-fee),
  // Active + published to Online Store. Line keys are `${Print Method}-${line-count}`; plus VID sides
  // and a flat feedlot-laser fee. The Allflex maps above are untouched.
  const FOFIA_LINE_FEES = {
    'Laser-1-line': '47134189715593',
    'Laser-2-lines': '47134189748361',
    'Laser-3-lines': '47134189781129',
    'Pitch Black-1-line': '47134189813897',
    'Pitch Black-2-lines': '47134189846665',
    'Pitch Black-3-lines': '47134189879433'
  };
  const FOFIA_SIDE_FEES = {
    'No Print': null,
    'VID 1 Side': '47134189912201',
    'VID 2 Sides': '47134189944969'
  };
  const FOFIA_FEEDLOT_LASER_FEE = '47134189977737';
  // Reverse/back-print add-on (order-form parity): back print follows the front method —
  // Laser $0.30 / Pitch Black ("Ink") $0.95. Added per-tag when the "print the back" box is ticked.
  const FOFIA_BACK_PRINT_FEES = {
    'Laser': '47200466403465',
    'Pitch Black': '47200466436233'
  };
  const FOFIA_ALL_FEE_IDS = [
    ...Object.values(FOFIA_LINE_FEES),
    ...Object.values(FOFIA_SIDE_FEES).filter(Boolean),
    FOFIA_FEEDLOT_LASER_FEE,
    ...Object.values(FOFIA_BACK_PRINT_FEES)
  ];

  const PRINT_FEE_VARIANT_IDS = [...ALL_SETUP_FEE_IDS, ...ALL_LINE_FEE_IDS, ...ALL_SIDE_FEE_IDS, ...FOFIA_ALL_FEE_IDS];
  const ALL_FEE_VARIANT_IDS = [SA_LEVY_VARIANT_ID, ...PRINT_FEE_VARIANT_IDS];

  // ===========================================================================
  // CART SECTION REFRESH (DAWN-NATIVE PATTERN)
  // ===========================================================================

  /**
   * SPACEY: Refresh all cart sections using Dawn's Section Rendering API pattern
   *
   * This implements Dawn's exact rendering mechanism:
   * - Fetches all cart sections in one request
   * - Replaces .drawer__inner (not cart-drawer-items) - Dawn's exact target
   * - Toggles is-empty class on all elements (Dawn's pattern)
   * - Updates cart drawer, cart page, and icon bubble
   *
   * Dawn's onCartUpdate() is incomplete - this implements the full update.
   *
   * @param {Object} debugLogger - Debug logger instance (optional)
   */
  async function refreshAllCartSections(debugLogger) {
    const debug = debugLogger || createDebugLogger('[CART REFRESH]');

    debug.log('=== REFRESHING ALL CART SECTIONS ===');

    try {
      // SPACEY: Fetch all cart sections that might need updating
      const sectionsToFetch = [
        'cart-drawer',
        'main-cart-items',
        'cart-icon-bubble'
      ];

      debug.log('Fetching sections:', sectionsToFetch.join(', '));
      const response = await fetch(`/?sections=${sectionsToFetch.join(',')}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.status}`);
      }

      const sections = await response.json();
      debug.log('Sections fetched successfully:', Object.keys(sections));

      let updateCount = 0;

      // SPACEY: Update cart drawer section (if exists)
      if (sections['cart-drawer']) {
        debug.log('Processing cart-drawer section...');
        debug.log('Section HTML length:', sections['cart-drawer'].length);

        // SPACEY: Follow Dawn's exact pattern - replace .drawer__inner not cart-drawer-items
        // Dawn: getSectionsToRender() returns {id: 'CartDrawer', selector: '.drawer__inner'}
        const cartDrawerElement = document.getElementById('CartDrawer');
        debug.log('CartDrawer element found:', !!cartDrawerElement);

        if (cartDrawerElement) {
          // Find the .drawer__inner element (what Dawn actually replaces)
          const drawerInner = cartDrawerElement.querySelector('.drawer__inner');
          debug.log('.drawer__inner element found:', !!drawerInner);

          if (drawerInner) {
            // SPACEY: Parse section and get .drawer__inner HTML using Dawn's exact method
            const parser = new DOMParser();
            const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');

            // Use Dawn's getSectionInnerHTML pattern
            const newDrawerInner = doc.querySelector('.drawer__inner');
            debug.log('New .drawer__inner in section:', !!newDrawerInner);

            if (newDrawerInner) {
              // Check if cart is empty
              const isEmpty = newDrawerInner.querySelector('.drawer__inner-empty') !== null;
              debug.log('Cart empty state (has empty-state div):', isEmpty);

              // Replace entire .drawer__inner content (Dawn's pattern)
              debug.log('Before update - drawer innerHTML length:', drawerInner.innerHTML.length);
              drawerInner.innerHTML = newDrawerInner.innerHTML;
              debug.log('After update - drawer innerHTML length:', drawerInner.innerHTML.length);
              updateCount++;
              debug.log('✓ Cart drawer .drawer__inner updated (Dawn pattern)');

              // SPACEY: Toggle is-empty class on cart-drawer wrapper (Dawn's pattern)
              const cartDrawerWrapper = document.querySelector('cart-drawer');
              if (cartDrawerWrapper) {
                cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
                debug.log('✓ Cart drawer is-empty class set to:', isEmpty);
              }

              // SPACEY: Also update cart footer if it exists (for /cart page)
              const cartFooter = document.getElementById('main-cart-footer');
              if (cartFooter) {
                cartFooter.classList.toggle('is-empty', isEmpty);
                debug.log('✓ Cart footer is-empty class set to:', isEmpty);
              }
            }
          }
        }
      } else {
        debug.log('No cart-drawer section in response');
      }

      // SPACEY: Update main cart items section (if exists - for /cart page)
      if (sections['main-cart-items']) {
        debug.log('Processing main-cart-items section...');

        const mainCartItems = document.getElementById('main-cart-items');
        debug.log('main-cart-items element found:', !!mainCartItems);

        if (mainCartItems) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['main-cart-items'], 'text/html');

          const newItems = doc.querySelector('cart-items');
          debug.log('cart-items element found in section:', !!newItems);

          if (newItems) {
            const isEmpty = newItems.classList.contains('is-empty');
            debug.log('Main cart items empty state:', isEmpty);

            mainCartItems.innerHTML = newItems.innerHTML;

            // SPACEY: Toggle is-empty class on main-cart-items (for cart page empty state)
            mainCartItems.classList.toggle('is-empty', isEmpty);

            updateCount++;
            debug.log('✓ Main cart items updated, is-empty:', isEmpty);
          }
        }
      } else {
        debug.log('No main-cart-items section in response');
      }

      // SPACEY: Update cart icon bubble (critical - shows item count)
      if (sections['cart-icon-bubble']) {
        debug.log('Processing cart-icon-bubble section...');
        debug.log('Section HTML length:', sections['cart-icon-bubble'].length);

        const cartBubble = document.getElementById('cart-icon-bubble');
        debug.log('cart-icon-bubble element found:', !!cartBubble);

        if (cartBubble) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');

          const newBubble = doc.querySelector('.shopify-section');
          debug.log('.shopify-section found in bubble section:', !!newBubble);

          if (newBubble) {
            debug.log('Before update - bubble HTML length:', cartBubble.innerHTML.length);
            debug.log('Before update - new bubble HTML length:', newBubble.innerHTML.length);
            cartBubble.innerHTML = newBubble.innerHTML;
            debug.log('After update - bubble HTML length:', cartBubble.innerHTML.length);
            updateCount++;
            debug.log('✓ Cart icon bubble updated');
          }
        }
      } else {
        debug.log('No cart-icon-bubble section in response');
      }

      debug.log(`✅ ALL CART SECTIONS REFRESHED (${updateCount} updates)`);
    } catch (error) {
      debug.error('!!! ERROR REFRESHING CART SECTIONS !!!', error);
      debug.error('Error stack:', error.stack);
      // Don't throw - let the cart modification complete even if UI refresh fails
    }
  }

  // ===========================================================================
  // EXPORTS
  // ===========================================================================

  window.CartSharedHelpers = {
    // Debug system
    createDebugLogger,
    DEBUG,

    // Fee variant IDs
    SA_LEVY_VARIANT_ID,
    SETUP_FEES,
    LINE_FEES,
    SIDE_FEES,
    ALL_SETUP_FEE_IDS,
    ALL_LINE_FEE_IDS,
    ALL_SIDE_FEE_IDS,
    FOFIA_LINE_FEES,
    FOFIA_SIDE_FEES,
    FOFIA_FEEDLOT_LASER_FEE,
    FOFIA_BACK_PRINT_FEES,
    FOFIA_ALL_FEE_IDS,
    PRINT_FEE_VARIANT_IDS,
    ALL_FEE_VARIANT_IDS,

    // UI refresh
    refreshAllCartSections
  };

  // console.log('[CART SHARED HELPERS] Loaded and exported to window.CartSharedHelpers');
})();
